import { Container, Row, Col, Form, Button, Breadcrumb, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import pageHeader from '../../assets/images/pageHeader.a8555e51.jpg';

const Contact = () => {
  const { t } = useTranslation();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    // Get reCAPTCHA token
    const recaptchaToken = await recaptchaRef.current?.executeAsync();

    if (!recaptchaToken) {
      setSubmitStatus({
        type: 'error',
        message: 'reCAPTCHA verification failed. Please try again.'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/contact-handler.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken
        })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({
          type: 'success',
          message: data.message
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        // Reset reCAPTCHA
        recaptchaRef.current?.reset();
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again later or contact us directly at info@eeuca.org'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Page Header */}
      <div
        className="page-header py-5"
        style={{
          backgroundImage: `url(${pageHeader})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <Container>
          <h1 className="text-white">{t('contact_us')}</h1>
          <Breadcrumb>
            <Breadcrumb.Item href="/">{t('home')}</Breadcrumb.Item>
            <Breadcrumb.Item active>{t('contact_us')}</Breadcrumb.Item>
          </Breadcrumb>
        </Container>
      </div>

      <Container className="my-5">
        <Row>
          <Col lg={6}>
            <h2>{t('contact_us_page.title')}</h2>
            <p className="lead">
              {t('contact_us_page.description')}
            </p>

            <div className="contact-info mt-4">
              <h4>{t('contact_us_page.contact_info')}</h4>
              <p>
                <strong>{t('contact_us_page.location')}:</strong><br />
                {t('contact_us_page.location_address').split('\n').map((line, i) => (
                  <span key={i}>{line}<br /></span>
                ))}
              </p>
              <p>
                <strong>{t('contact_us_page.email')}:</strong><br />
                info@eeuca.org
              </p>
            </div>
          </Col>

          <Col lg={6}>
            <h3>{t('contact_us_page.send_message')}</h3>

            {submitStatus.type && (
              <Alert variant={submitStatus.type === 'success' ? 'success' : 'danger'} className="mb-3">
                {submitStatus.message}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>{t('contact_us_page.name')}</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact_us_page.name_placeholder') as string}
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('contact_us_page.email')}</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact_us_page.email_placeholder') as string}
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('contact_us_page.subject')}</Form.Label>
                <Form.Control
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('contact_us_page.subject_placeholder') as string}
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('contact_us_page.message')}</Form.Label>
                <Form.Control
                  as="textarea"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder={t('contact_us_page.message_placeholder') as string}
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Invisible reCAPTCHA */}
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
              />

              <Button variant="primary" type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : t('contact_us_page.send_button')}
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Contact;
